import { ref, watch, onMounted } from 'vue'
import { useI18n } from '../i18n'

const initialized = ref(false)

export function useCookieConsent() {
  const { locale } = useI18n()

  const getTextConfig = (lang) => {
    const texts = {
      en: {
        prompt: {
          description: 'We use cookies to enhance your experience and analyze website traffic. By continuing to use our website, you agree to our use of cookies.',
          acceptAllButtonText: 'Accept All',
          acceptAllButtonAccessibleLabel: 'Accept all cookies',
          rejectNonEssentialButtonText: 'Reject Non-Essential',
          rejectNonEssentialButtonAccessibleLabel: 'Reject all non-essential cookies',
          preferencesButtonText: 'Preferences',
          preferencesButtonAccessibleLabel: 'Manage cookie preferences',
        },
        preferences: {
          title: 'Cookie Preferences',
          description: 'Choose which cookies you want to accept.',
          saveButtonText: 'Save and Close',
          saveButtonAccessibleLabel: 'Save your cookie preferences',
          creditLinkText: 'Silktide Consent Manager',
          creditLinkAccessibleLabel: 'Visit Silktide Consent Manager',
        },
      },
      zh: {
        prompt: {
          description: '我们使用Cookie来提升您的体验并分析网站流量。继续使用我们的网站即表示您同意我们使用Cookie。',
          acceptAllButtonText: '全部接受',
          acceptAllButtonAccessibleLabel: '接受所有Cookie',
          rejectNonEssentialButtonText: '拒绝非必要Cookie',
          rejectNonEssentialButtonAccessibleLabel: '拒绝所有非必要Cookie',
          preferencesButtonText: '偏好设置',
          preferencesButtonAccessibleLabel: '管理Cookie偏好设置',
        },
        preferences: {
          title: 'Cookie偏好设置',
          description: '选择您想接受的Cookie类型。',
          saveButtonText: '保存并关闭',
          saveButtonAccessibleLabel: '保存您的Cookie偏好设置',
          creditLinkText: 'Silktide同意管理器',
          creditLinkAccessibleLabel: '访问Silktide同意管理器',
        },
      },
      es: {
        prompt: {
          description: 'Usamos cookies para mejorar su experiencia y analizar el tráfico del sitio web. Al continuar usando nuestro sitio web, acepta nuestro uso de cookies.',
          acceptAllButtonText: 'Aceptar Todo',
          acceptAllButtonAccessibleLabel: 'Aceptar todas las cookies',
          rejectNonEssentialButtonText: 'Rechazar No Esenciales',
          rejectNonEssentialButtonAccessibleLabel: 'Rechazar todas las cookies no esenciales',
          preferencesButtonText: 'Preferencias',
          preferencesButtonAccessibleLabel: 'Administrar preferencias de cookies',
        },
        preferences: {
          title: 'Preferencias de Cookies',
          description: 'Elija qué cookies desea aceptar.',
          saveButtonText: 'Guardar y Cerrar',
          saveButtonAccessibleLabel: 'Guardar sus preferencias de cookies',
          creditLinkText: 'Silktide Consent Manager',
          creditLinkAccessibleLabel: 'Visite Silktide Consent Manager',
        },
      },
    }
    return texts[lang] || texts.en
  }

  const initConsentManager = () => {
    if (initialized.value) return
    if (typeof window === 'undefined') return
    if (!window.silktideConsentManager) {
      console.warn('Silktide Consent Manager not loaded')
      return
    }

    const textConfig = getTextConfig(locale.value)

    window.silktideConsentManager.init({
      consentTypes: [
        {
          id: 'essential',
          label: { en: 'Essential', zh: '必要Cookie', es: 'Esenciales' }[locale.value] || 'Essential',
          description: {
            en: 'These are necessary for the website to function and cannot be switched off.',
            zh: '这些是网站正常运行所必需的，无法关闭。',
            es: 'Estas son necesarias para que el sitio web funcione y no se pueden desactivar.',
          }[locale.value],
          required: true,
        },
        {
          id: 'analytics',
          label: { en: 'Analytics', zh: '分析Cookie', es: 'Análisis' }[locale.value] || 'Analytics',
          description: {
            en: 'These help us understand how visitors interact with the website.',
            zh: '这些帮助我们了解访客如何与网站互动。',
            es: 'Estas nos ayudan a comprender cómo los visitantes interactúan con el sitio web.',
          }[locale.value],
          defaultValue: false,
          gtag: 'analytics_storage',
        },
      ],
      text: textConfig,
      prompt: {
        position: 'bottomRight',
      },
      icon: {
        position: 'bottomLeft',
      },
      backdrop: {
        show: false,
      },
      onAcceptAll: () => {
        console.log('All cookies accepted')
      },
      onRejectAll: () => {
        console.log('Non-essential cookies rejected')
      },
    })

    initialized.value = true
  }

  const openPreferences = () => {
    if (window.silktideConsentManager) {
      const instance = window.silktideConsentManager.getInstance()
      if (instance) {
        instance.toggleModal(true)
      }
    }
  }

  const updateLanguage = () => {
    if (!initialized.value) return
    if (window.silktideConsentManager) {
      const textConfig = getTextConfig(locale.value)
      window.silktideConsentManager.update({
        text: textConfig,
      })
    }
  }

  onMounted(() => {
    const loadAndInit = () => {
      if (window.silktideConsentManager) {
        initConsentManager()
      } else {
        setTimeout(loadAndInit, 100)
      }
    }
    loadAndInit()
  })

  watch(locale, () => {
    if (initialized.value) {
      updateLanguage()
    }
  })

  return {
    initConsentManager,
    openPreferences,
  }
}
